import type { ButtonType } from "@similarweb/ui-components";
import { IconButton } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import AddToListDropdownContainer from "app/pages/sales-intelligence/sub-modules/opportunities/components/miscellaneous/AddToListDropdown/AddToListDropdownContainer";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { openHooksModalV5 } from "app/services/ModalService";

import ListIconButtonTooltipContainer from "../ListIconButton/ListIconButtonTooltipContainer";

import { StyledAddToListDropdownWrapper } from "./styles";
import getTooltipTranslation from "./utils";

type AddWebsiteToListButtonBaseProps = {
    domain: string;
    disabledText?: string;
    type?: ButtonType;
    isShortContent?: boolean;
    onButtonClick?: () => void;
    onButtonHover?: () => void;
};

type AddWebsiteToListButtonProps = AddWebsiteToListButtonBaseProps & {
    listsWithDomain: string[];
    isListSizeQuotaReached: boolean;
    isTrialTooltip?: boolean;
    limitOfWebsitesToTrack?: number;
    isDisabled: boolean;
    disabledListsNames: string[];
    onHookButtonClick?: () => void;
};

const AddWebsiteToListButton = (props: AddWebsiteToListButtonProps) => {
    const {
        domain,
        disabledText,
        type,
        isShortContent,
        disabledListsNames,
        isListSizeQuotaReached,
        isTrialTooltip = false,
        limitOfWebsitesToTrack,
        listsWithDomain,
        isDisabled,
        onButtonClick,
        onButtonHover,
        onHookButtonClick,
    } = props;

    const translate = useTranslation();

    const showDefaultButtonText = listsWithDomain.length === 0 && !isShortContent;

    const handleListUpdate = () => {
        companyAnalysisTrackingService.overview.trackAddToListSelect("existing list");
    };

    const handleListCreated = () => {
        companyAnalysisTrackingService.overview.trackAddToListSelect("new list");
    };

    const renderTooltipContent = () => {
        if (!listsWithDomain.length) {
            if (showDefaultButtonText) {
                return undefined;
            }

            return <div>{translate("si.pages.account_review.add_to_list_button.tooltip")}</div>;
        }

        return (
            <div>
                <Box margin={"0 0 16px 0"}>{getTooltipTranslation(translate, listsWithDomain)}</Box>
                <div>{translate("si.pages.account_review.add_to_list_button.tooltip.addHint")}</div>
            </div>
        );
    };

    const renderListIconButton = (onClick: () => void) => {
        const buttonText = showDefaultButtonText
            ? translate("si.pages.account_review.add_to_list_button.addToListTitle")
            : translate("si.pages.account_review.add_to_list_button.title", { count: listsWithDomain.length });

        return (
            <ListIconButtonTooltipContainer
                onClick={() => {
                    onButtonClick?.();
                    onClick();
                }}
                onMouseEnter={onButtonHover}
                buttonText={buttonText}
                tooltipContent={renderTooltipContent()}
                type={type}
                isDisabled={false}
            />
        );
    };

    if (listsWithDomain.length === 0 && isDisabled) {
        return (
            <IconButton
                type="flatTrial"
                iconName="sparkles"
                onClick={() => {
                    onHookButtonClick?.();
                    openHooksModalV5("SalesSolutionLists");
                }}
            >
                {translate("si.pages.account_review.add_to_list_button.addToListTitle")}
            </IconButton>
        );
    }

    return (
        <StyledAddToListDropdownWrapper>
            <AddToListDropdownContainer
                className={`add-to-list-dropdown-${domain}`}
                disabledText={disabledText}
                disabledLists={disabledListsNames}
                domains={[domain]}
                renderDropdownButton={renderListIconButton}
                onListUpdate={handleListUpdate}
                onFinishedCreatingList={handleListCreated}
                isDisabled={isListSizeQuotaReached}
            />
        </StyledAddToListDropdownWrapper>
    );
};

export default AddWebsiteToListButton;
